import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const MONEY_FORMAT = "#,##0.00";
const DATE_FORMAT = "dd/mm/yyyy";
const DATETIME_FORMAT = "dd/mm/yyyy hh:mm";

// "23,500.00", "$ 1,120.00", "-$ 10.50", "12"
const NUMBER_REGEX = /^(-)?\s*\$?\s*(-)?\s*(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?$/;
const PERCENT_REGEX = /^(-?\d+(?:\.\d+)?)\s*%$/;
// "12/07/2026" o "12/07/2026 · 13:51"
const DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s*·\s*(\d{2}):(\d{2}))?$/;

const EXCEL_EPOCH = Date.UTC(1899, 11, 30);

// Serial de Excel calculado en UTC para que la zona horaria del navegador no mueva el día.
const toExcelSerial = (y: number, m: number, d: number, h = 0, min = 0) =>
  (Date.UTC(y, m - 1, d, h, min) - EXCEL_EPOCH) / 86400000;

const parseCell = (text: string): XLSX.CellObject | null => {
  const value = text.replace(/ /g, " ").trim();
  if (!value) return null;

  const date = value.match(DATE_REGEX);
  if (date) {
    const [, d, m, y, h, min] = date;
    return {
      t: "n",
      v: toExcelSerial(+y, +m, +d, h ? +h : 0, min ? +min : 0),
      z: h ? DATETIME_FORMAT : DATE_FORMAT,
    };
  }

  const percent = value.match(PERCENT_REGEX);
  if (percent) {
    return { t: "n", v: +percent[1] / 100, z: "0.00%" };
  }

  const number = value.match(NUMBER_REGEX);
  if (number) {
    const [, sign1, sign2, int, dec] = number;
    // Conserva folios/códigos con ceros a la izquierda como texto
    if (!dec && !int.includes(",") && int.length > 1 && int.startsWith("0")) {
      return null;
    }
    const n = +`${int.replace(/,/g, "")}${dec || ""}` * (sign1 || sign2 ? -1 : 1);
    return { t: "n", v: n, z: dec || int.includes(",") ? MONEY_FORMAT : undefined };
  }

  return null;
};

// Convierte a número/fecha real las celdas de texto con montos, porcentajes o fechas DD/MM/YYYY
const normalizeSheet = (worksheet: XLSX.WorkSheet) => {
  Object.keys(worksheet).forEach((ref) => {
    if (ref.startsWith("!")) return;
    const cell = worksheet[ref] as XLSX.CellObject;
    if (cell.t !== "s" || typeof cell.v !== "string") return;
    const parsed = parseCell(cell.v);
    if (parsed) worksheet[ref] = parsed;
  });
  return worksheet;
};

const save = (worksheet: XLSX.WorkSheet) => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Hoja1");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: "application/octet-stream" });

  saveAs(data, `${new Date().getTime()}.xlsx`);
};

export const ExcelService = {
  fromTable: (tableId: string) => {
    const table = document.getElementById(tableId);
    if (!table) {
      console.error("Tabla no encontrada");
      return;
    }
    // raw: evita que SheetJS interprete fechas DD/MM como MM/DD
    save(normalizeSheet(XLSX.utils.table_to_sheet(table, { raw: true })));
  },
  fromJson: (rows: any[]) => {
    save(normalizeSheet(XLSX.utils.json_to_sheet(rows)));
  },
};
